import { Injectable } from '@angular/core';
import { PortalApiService } from '../../../shared/services/api/portal-api.service';
import { Observable } from 'rxjs';
import { Assessment } from '../models/assessment';
import { Question } from '../models/question';
import { finalize } from 'rxjs/operators';

const apiEndpoints = {
  assessment: 'cmp/{id}/assessment/{assessmentId}',
  question: 'cmp/{id}/question/{questionId}',
  startAssessment: 'cmp/{id}/assessment/{assessmentId}',
  startQuestion: 'cmp/{id}/questionresponse',
  answerQuestion: 'cmp/{id}/questionresponse/{questionResponseId}',
  confidenceAndRelevance: 'cmp/{id}/questionresponse/{questionResponseId}/relevance',
  questionDetails: 'cmp/{id}/question/{questionId}/detail',
  dashboardAssessments: 'cmp/{id}/assessments',
  fetchAssessmentResult: 'cmp/{id}/assessmentResult/{assessmentResultId}',
  screenshot: 'cmp/{id}/screencap/{action}',
  updateOptinStatus: 'practitioner/{id}/isoptin',
  referenceDetail: 'cmp/EmailReferences/{personId}/{questionId}',
  emailAssessmentReport: 'cmp/emailCMPAsessmentReport/{personId}?email={email}'
};

@Injectable()
export class AssessmentService {

  private assessment: Assessment;
  private assessmentResultId: string;
  private questionResponseId: string;
  private question: Question;
  private selectedAnswer: string;

  constructor(private portalApiService: PortalApiService) {
    this.assessment = new Assessment();
  }

  generateCMPAssessmentReport(personId: string, email: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.emailAssessmentReport.replace('{personId}', personId).replace('{email}', email))
      .pipe(finalize(() => null));
  }
  
  getAssessment(personId: string, assessmentId: string): Observable<any> {
    if (this.assessment && this.assessment.assessmentId !== assessmentId) {
      return this.portalApiService
        .get(apiEndpoints.assessment.replace('{id}', personId).replace('{assessmentId}', assessmentId))
        .pipe(finalize(() => null));
    }
  }
  
  getAssessmentQuestion(personId: string, questionId: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.question.replace('{id}', personId).replace('{questionId}', questionId))
      .pipe(finalize(() => null));
  }
  
  startQuestion(personId: string, questionId: string, assessmentResultId: string): Observable<any> {
    return this.portalApiService
      .post(apiEndpoints.startQuestion.replace('{id}', personId), { questionId, assessmentResultId })
      .pipe(finalize(() => null));
  }
  
  startAssessment(personId: string, assessmentId: string): Observable<any> {
    return this.portalApiService
      .post(apiEndpoints.startAssessment.replace('{id}', personId).replace('{assessmentId}', assessmentId), { assessmentId })
      .pipe(finalize(() => null));
  }
  
  answerQuestion(personId: string, questionOptionId: string, questionResponseId: string): Observable<any> {
    return this.portalApiService
      .put(apiEndpoints.answerQuestion.replace('{id}', personId).replace('{questionResponseId}', questionResponseId), { questionOptionId })
      .pipe(finalize(() => null));
  }
  
  submitConfidenceRelevance(personId: string, questionResponseId: string, confidenceObj): Observable<any> {
    return this.portalApiService
      .put(apiEndpoints.confidenceAndRelevance.replace('{id}', personId).replace('{questionResponseId}', questionResponseId), confidenceObj)
      .pipe(finalize(() => null));
  }
  
  getQuestionDetails(personId: string, questionId: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.questionDetails.replace('{id}', personId).replace('{questionId}', questionId))
      .pipe(finalize(() => null));
  }
  
  fetchDashboardAssessments(personId: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.dashboardAssessments.replace('{id}', personId))
      .pipe(finalize(() => null));
  }
  
  fetchAssessmentResult(personId: string, assessmentResultId: string): Observable<any> {
    return this.portalApiService
      .get(apiEndpoints.fetchAssessmentResult.replace('{id}', personId).replace('{assessmentResultId}', assessmentResultId))
      .pipe(finalize(() => null));
  }
  
  warnUser(personId: string, action: string, questionId?: string): Observable<any> {
    return this.portalApiService
      .post(apiEndpoints.screenshot.replace('{id}', personId).replace('{action}', action), { questionId })
      .pipe(finalize(() => null));
  }
  
  updateOptin(personId: string, optinObj): Observable<any> {
    return this.portalApiService
      .post(apiEndpoints.updateOptinStatus.replace('{id}', personId), optinObj)
      .pipe(finalize(() => null));
  }

  setAssessment(assessment: Assessment) {
    this.assessment = assessment;
  }

  getAssessmentObj(assessmentId) {
    if (this.assessment && String(this.assessment.assessmentId) === assessmentId) {
      return this.assessment;
    }
  }

  setAssessmentResultId(assessmentResultId: string) {
    this.assessmentResultId = assessmentResultId;
  }

  getAssessmentResultId() {
    return this.assessmentResultId;
  }

  setQuestionResponseId(questionResponseId) {
    this.questionResponseId = questionResponseId;
  }

  getQuestionResponseId() {
    return this.questionResponseId;
  }

  setQuestion(question: Question) {
    this.question = question;
  }

  getQuestion() {
    return this.question;
  }

  setSelectedAnswer(questionOptionId) {
    this.selectedAnswer = questionOptionId;
  }

  getSelectedAnswer() {
    return this.selectedAnswer;
  }

  destroyQuestion() {
    this.question = new Question();
    this.selectedAnswer = null;
    this.questionResponseId = null;
    this.assessment.currentQuestionSequence++;
  }

  getEmailReferenceDetail(personId: string, questionId: string) {    
    return this.portalApiService.get(apiEndpoints.referenceDetail.replace('{personId}', personId).replace('{questionId}', questionId));
  }
}
